import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import LocationPicker from "../components/LocationPicker";
import "./css/AddWork.css";
import noWorkImage from "../assets/images/no_work.png";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;

const MAX_DOCUMENTS = 5;
const MAX_FILE_SIZE = 5 * 1096000; // ~5MB per requirements

const ALLOWED_DOC_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const EMIRATES = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
];

const formatDate = (date) => date.toISOString().split("T")[0];

export default function EditWork() {
  const navigate = useNavigate();
  const { id } = useParams();
  const documentInputRef = useRef(null);

  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [loading, setLoading] = useState(true);
  const [workNotFound, setWorkNotFound] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [subCategorySearch, setSubCategorySearch] = useState("");
  const [specialisations, setSpecialisations] = useState([]);
  const [selectedSpecialisations, setSelectedSpecialisations] = useState([]);
  const [specialisationSearch, setSpecialisationSearch] = useState("");

  const [title, setTitle] = useState("");
  const [quoteLastDate, setQuoteLastDate] = useState("");
  const [description, setDescription] = useState("");

  const [sameAsProfile, setSameAsProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  const [hasAppliedProfileAddress, setHasAppliedProfileAddress] = useState(false);

  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");

  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const [workAmountMin, setWorkAmountMin] = useState("");
  const [workAmountMax, setWorkAmountMax] = useState("");

  const [documents, setDocuments] = useState([]); // New documents to upload
  const [existingDocuments, setExistingDocuments] = useState([]); // Existing documents from server
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const [errors, setErrors] = useState({});

  // Check if work has quotes (for disabling fields)
  const [hasQuotes, setHasQuotes] = useState(false);
  const [workStatus, setWorkStatus] = useState(null);
  const [workData, setWorkData] = useState(null); // Store work data for category population

  const titleFieldRef = useRef(null);
  const categoryFieldRef = useRef(null);
  const subCategoriesFieldRef = useRef(null);
  const specialisationsFieldRef = useRef(null);
  const quoteDateFieldRef = useRef(null);
  const descriptionFieldRef = useRef(null);
  const workAmountFieldRef = useRef(null);
  const address1FieldRef = useRef(null);
  const address2FieldRef = useRef(null);
  const cityFieldRef = useRef(null);
  const stateFieldRef = useRef(null);
  const pincodeFieldRef = useRef(null);
  const locationFieldRef = useRef(null);

  // Listen for language changes triggered elsewhere
  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("app_language") || "en");
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Translations
  const t = useMemo(() => {
    return {
      en: {
        loading: "Loading",
        pageTitle: "Edit Work",
        heading: "Work Information",
        title: "Title",
        titlePlaceholder: "Enter work title",
        categories: "Categories",
        subCategories: "Sub Categories",
        specialisations: "Specialisations",
        selectedSubCategories: "Selected Sub Categories",
        selectedSpecialisations: "Selected Specialisations",
        searchSubCategories: "Search sub categories...",
        lastDate: "Last Date for Quote",
        selectDate: "Select date",
        description: "Description",
        descriptionPlaceholder: "Enter work description",
        workAmount: "Work Amount (AED)",
        minAmount: "Min",
        maxAmount: "Max",
        documents: "Documents",
        documentsInfo: `Upload up to ${MAX_DOCUMENTS} documents (PDF, JPG, PNG, etc., max 5MB each)`,
        clickToUpload: "Click here to upload documents",
        locationDetails: "Location Details",
        sameAsProfile: "Same as profile address",
        address1: "Address Line 1",
        address2: "Address Line 2",
        city: "City",
        state: "State",
        selectState: "Select state",
        pincode: "Pincode",
        locationOnMap: "Location on Map",
        selectLocation: "Select Location",
        locationSelected: "Location Selected",
        addWork: "Update Work",
        noCategories: "No categories available",
        submitSuccess: "Work updated successfully",
        submitError: "Failed to update work",
        editError: "Failed to load work details",
        workNotFound: "Work not found",
        cannotEditCompleted: "Cannot edit completed work",
        deleteDocument: "Delete Document",
        deletingDocument: "Deleting...",
        documentDeleted: "Document deleted successfully",
        documentDeleteError: "Failed to delete document",
        error: "Error",
        quotesWarning: "Quotes have been received for this work. Only the quote last date can be changed.",
      },
      ar: {
        loading: "جاري التحميل",
        pageTitle: "تعديل العمل",
        heading: "معلومات العمل",
        title: "العنوان",
        titlePlaceholder: "أدخل عنوان العمل",
        categories: "الفئات",
        subCategories: "الفئات الفرعية",
        specialisations: "التخصصات",
        selectedSubCategories: "الفئات الفرعية المحددة",
        selectedSpecialisations: "التخصصات المحددة",
        searchSubCategories: "ابحث عن الفئات الفرعية...",
        lastDate: "آخر موعد للعرض",
        selectDate: "اختر التاريخ",
        description: "الوصف",
        descriptionPlaceholder: "أدخل وصف العمل",
        workAmount: "مبلغ العمل (درهم)",
        minAmount: "الحد الأدنى",
        maxAmount: "الحد الأقصى",
        documents: "المستندات",
        documentsInfo: `تحميل حتى ${MAX_DOCUMENTS} مستندات (PDF, JPG, PNG, إلخ، بحد أقصى 5 ميجابايت لكل منها)`,
        clickToUpload: "انقر هنا لتحميل المستندات",
        locationDetails: "تفاصيل الموقع",
        sameAsProfile: "نفس عنوان الملف الشخصي",
        address1: "سطر العنوان 1",
        address2: "سطر العنوان 2",
        city: "المدينة",
        state: "الولاية",
        selectState: "اختر الولاية",
        pincode: "الرمز البريدي",
        locationOnMap: "الموقع على الخريطة",
        selectLocation: "اختر الموقع",
        locationSelected: "تم اختيار الموقع",
        addWork: "تحديث العمل",
        noCategories: "لا توجد فئات متاحة",
        submitSuccess: "تم تحديث العمل بنجاح",
        submitError: "فشل تحديث العمل",
        editError: "فشل تحميل تفاصيل العمل",
        workNotFound: "العمل غير موجود",
        cannotEditCompleted: "لا يمكن تعديل العمل المكتمل",
        deleteDocument: "حذف المستند",
        deletingDocument: "جاري الحذف...",
        documentDeleted: "تم حذف المستند بنجاح",
        documentDeleteError: "فشل حذف المستند",
        error: "خطأ",
        quotesWarning: "تم استلام عروض أسعار لهذا العمل. يمكن تغيير تاريخ انتهاء العروض فقط.",
      },
    }[lang];
  }, [lang]);

  // Fetch categories and profile
  useEffect(() => {
    const loadData = async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
          showToast("Unauthorized. Please login again.", "error");
          navigate("/");
          return;
        }

        const [categoriesData, profileData] = await Promise.allSettled([
          apiFetch(`${USER_BASE_URL}/${VERSION}/sub-category/`, "GET", null, {}),
          apiFetch(`${USER_BASE_URL}/${VERSION}/profile/`, "GET", null, {}),
        ]);

        if (categoriesData.status === "fulfilled") {
          const response = categoriesData.value;
          if (response?.response) {
            const payload = response.response;
            if (Array.isArray(payload)) {
              setCategories(payload);
            } else {
              setCategories(payload.categories || []);
              setSpecialisations(payload.specialisations || []);
            }
          }
        }
        setCategoriesLoaded(true); // Mark categories as loaded, even if empty

        if (profileData.status === "fulfilled") {
          const response = profileData.value;
          if (response?.response) {
            setProfile(response.response);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setCategoriesLoaded(true); // Mark as loaded even on error
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [USER_BASE_URL, VERSION]);

  // Fetch work details - only after categories are loaded
  useEffect(() => {
    if (!id || !categoriesLoaded) {
      // If categories haven't loaded yet, keep loading state
      // If id is missing, set loading to false so we can show error
      if (!id) {
        setLoading(false);
      }
      return; // Wait for categories to load
    }

    const fetchWorkDetails = async () => {
      try {
        setLoading(true);
        setWorkNotFound(false);

        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/work/`,
          "POST",
          JSON.stringify({ id }),
          { "Content-Type": "application/json" }
        );

        if (data && data.response_code === 200 && data.response) {
          const work = data.response;
          
          // Store work data for category population
          setWorkData(work);

          // Check if work is completed - redirect if so
          if (work.status === "completed") {
            showToast(t.cannotEditCompleted, "error");
            navigate(-1);
            return;
          }

          setWorkStatus(work.status);

          // Check if work has quotes
          const hasQuotesReceived = work.quotes_received && work.quotes_received.length > 0;
          setHasQuotes(hasQuotesReceived);

          // Populate form fields (except category/sub-categories which will be populated after categories load)
          if (work.title) setTitle(work.title);
          if (work.quote_last_date) {
            const date = new Date(work.quote_last_date);
            setQuoteLastDate(formatDate(date));
          }
          if (work.description) setDescription(work.description);
          if (work.work_amount) {
            if (work.work_amount.min !== undefined) setWorkAmountMin(String(work.work_amount.min));
            if (work.work_amount.max !== undefined) setWorkAmountMax(String(work.work_amount.max));
          }
          setSameAsProfile(false);
          if (work.address1) setAddress1(work.address1);
          if (work.address2) setAddress2(work.address2);
          if (work.city) setCity(work.city);
          if (work.state) setState(work.state);
          if (work.pincode !== undefined && work.pincode !== null) setPincode(String(work.pincode));
          if (work.location_coordinates && Array.isArray(work.location_coordinates) && work.location_coordinates.length === 2) {
            setLatitude(Number(work.location_coordinates[1])); // API returns [lng, lat], state expects [lat, lng]
            setLongitude(Number(work.location_coordinates[0]));
          }
          // Set existing documents
          if (work.documents && Array.isArray(work.documents)) {
            setExistingDocuments(work.documents);
          }
        } else {
          setWorkNotFound(true);
        }
      } catch (error) {
        console.error("Error fetching work details:", error);
        setWorkNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, USER_BASE_URL, VERSION, categoriesLoaded]);

  // Populate category and sub-categories when categories are loaded and work data is available
  useEffect(() => {
    if (!categoriesLoaded || !categories.length || !workData) return;

    // Handle category and sub-categories
    // If category_id is not present, extract it from sub_categories
    let categoryId = null;
    if (workData.category_id) {
      categoryId = typeof workData.category_id === "object" ? workData.category_id._id : workData.category_id;
    } else if (workData.sub_categories && Array.isArray(workData.sub_categories) && workData.sub_categories.length > 0) {
      // Extract category_id from first sub_category
      const firstSubCat = workData.sub_categories[0];
      if (firstSubCat && typeof firstSubCat === "object" && firstSubCat.category_id) {
        categoryId = typeof firstSubCat.category_id === "object" 
          ? firstSubCat.category_id._id 
          : firstSubCat.category_id;
      }
    }
    
    // Only set if categoryId is found and exists in categories
    if (categoryId) {
      const categoryExists = categories.find(cat => cat._id === categoryId);
      if (categoryExists) {
        // Only update if not already set or if it's different
        if (selectedCategoryId !== categoryId) {
          setSelectedCategoryId(categoryId);
        }
        
        // Populate sub-categories (always update to ensure they match work data)
        if (workData.sub_categories && Array.isArray(workData.sub_categories) && workData.sub_categories.length > 0) {
          const subCatIds = workData.sub_categories.map(sc => {
            if (typeof sc === "object" && sc._id) {
              return sc._id;
            }
            return sc;
          }).filter(id => id); // Filter out any invalid IDs
          
          // Only update if different
          const currentSubCatIds = JSON.stringify(selectedSubCategories.sort());
          const newSubCatIds = JSON.stringify(subCatIds.sort());
          if (currentSubCatIds !== newSubCatIds) {
            setSelectedSubCategories(subCatIds);
          }
        }
      }
    }
    // Populate specialisations from work data
    if (workData.specialisations && Array.isArray(workData.specialisations) && workData.specialisations.length > 0) {
      const specIds = workData.specialisations.map((s) =>
        typeof s === "object" && s._id ? s._id : s
      ).filter(Boolean);
      if (specIds.length) {
        const currentSpecIds = JSON.stringify(selectedSpecialisations.sort());
        const newSpecIds = JSON.stringify(specIds.sort());
        if (currentSpecIds !== newSpecIds) {
          setSelectedSpecialisations(specIds);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, workData]);

  // Active category
  const activeCategory = useMemo(() => {
    return categories.find((cat) => cat._id === selectedCategoryId) || null;
  }, [categories, selectedCategoryId]);

  // Filtered sub categories
  const filteredSubCategories = useMemo(() => {
    if (!activeCategory || !activeCategory.sub_categories) return [];
    const search = subCategorySearch.toLowerCase();
    return activeCategory.sub_categories.filter((subCat) =>
      subCat.name.toLowerCase().includes(search)
    );
  }, [activeCategory, subCategorySearch]);

  const filteredSpecialisations = useMemo(() => {
    if (!specialisations || specialisations.length === 0) return [];
    if (!specialisationSearch.trim()) return specialisations;
    const query = specialisationSearch.toLowerCase();
    return specialisations.filter((spec) =>
      spec.name?.toLowerCase().includes(query)
    );
  }, [specialisations, specialisationSearch]);

  // Date calculations
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDate = formatDate(today);
  const maxDate = formatDate(
    new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
  );

  // Toggle sub category
  const toggleSubCategory = (subCatId) => {
    setSelectedSubCategories((prev) => {
      if (prev.includes(subCatId)) {
        return prev.filter((id) => id !== subCatId);
      } else {
        return [...prev, subCatId];
      }
    });
  };

  // Remove sub category
  const removeSubCategory = (subCatId) => {
    setSelectedSubCategories((prev) => prev.filter((id) => id !== subCatId));
  };

  const toggleSpecialisation = (specId) => {
    setSelectedSpecialisations((prev) =>
      prev.includes(specId) ? prev.filter((id) => id !== specId) : [...prev, specId]
    );
  };

  const removeSpecialisation = (specId) => {
    setSelectedSpecialisations((prev) => prev.filter((id) => id !== specId));
  };

  // Validate document
  const validateDocument = (file) => {
    if (!ALLOWED_DOC_TYPES.includes(file.type)) {
      return { valid: false, error: "Invalid file type" };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: "File size exceeds 5MB" };
    }
    return { valid: true };
  };

  // Handle document select
  const handleDocumentSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const currentTotal = existingDocuments.length + documents.length;
    if (currentTotal + files.length > MAX_DOCUMENTS) {
      showToast(`Maximum ${MAX_DOCUMENTS} documents allowed`, "error");
      return;
    }

    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      const validation = validateDocument(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      showToast(
        `Invalid files: ${invalidFiles.join(", ")}. Max size: 5MB, allowed types: PDF, JPG, PNG, etc.`,
        "error"
      );
    }

    if (validFiles.length > 0) {
      setDocuments((prev) => [...prev, ...validFiles]);
    }

    // Reset input
    if (documentInputRef.current) {
      documentInputRef.current.value = "";
    }
  };

  // Remove new document
  const removeDocument = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  // Delete existing document
  const handleDeleteDocument = async (docId) => {
    if (!docId || !id) return;

    setDeletingDocumentId(docId);
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/work/element-delete/${id}`,
        "POST",
        JSON.stringify({ key: docId }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200) {
        setExistingDocuments((prev) => prev.filter((doc) => doc.id !== docId));
        showToast(t.documentDeleted, "success");
      } else {
        showToast(data?.message || t.documentDeleteError, "error");
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      showToast(error.message || t.documentDeleteError, "error");
    } finally {
      setDeletingDocumentId(null);
    }
  };

  // Handle location select
  const handleLocationSelect = (lat, lng) => {
    setLatitude(lat);
    setLongitude(lng);
    setShowLocationPicker(false);
    if (errors.location) {
      setErrors((prev) => ({ ...prev, location: null }));
    }
  };

  // Handle same as profile toggle
  const handleSameAsProfileToggle = (checked) => {
    setSameAsProfile(checked);
    if (checked && profile) {
      setHasAppliedProfileAddress(false); // Will trigger useEffect to apply profile address
    } else if (!checked && profile && hasAppliedProfileAddress) {
      // Clear fields if unchecking and profile address was applied
      setAddress1("");
      setAddress2("");
      setCity("");
      setState("");
      setPincode("");
      setLatitude(null);
      setLongitude(null);
      setHasAppliedProfileAddress(false);
    }
  };

  // Apply profile details when checkbox is selected
  useEffect(() => {
    if (!profile || !sameAsProfile || hasAppliedProfileAddress) {
      return;
    }
    if (profile.address1) setAddress1(profile.address1);
    if (profile.address2) setAddress2(profile.address2);
    if (profile.city) setCity(profile.city);
    if (profile.state) setState(profile.state);
    if (profile.pincode !== undefined && profile.pincode !== null) setPincode(String(profile.pincode));
    if (profile.location_coordinates && Array.isArray(profile.location_coordinates) && profile.location_coordinates.length === 2) {
      // Profile location_coordinates is [longitude, latitude]
      const [lng, lat] = profile.location_coordinates;
      setLatitude(Number(lat));
      setLongitude(Number(lng));
    }
    setHasAppliedProfileAddress(true);
  }, [profile, sameAsProfile, hasAppliedProfileAddress]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = "Title is required";
    } else if (title.trim().length > 50) {
      newErrors.title = "Title must be at most 50 characters";
    }

    if (!selectedCategoryId) {
      newErrors.category = "Category is required";
    }

    if (selectedSubCategories.length === 0) {
      newErrors.subCategories = "At least one sub-category is required";
    }

    if (selectedSpecialisations.length === 0) {
      newErrors.specialisations = "At least one specialisation is required";
    }

    if (!quoteLastDate) {
      newErrors.quoteLastDate = "Quote last date is required";
    } else {
      const selectedDate = new Date(quoteLastDate);
      const todayDate = new Date(today);
      const maxDateDate = new Date(maxDate);

      if (selectedDate < todayDate) {
        newErrors.quoteLastDate = "Quote last date must be a future date";
      } else if (selectedDate > maxDateDate) {
        newErrors.quoteLastDate = "Quote last date must be within 30 days";
      }
    }

    if (!description.trim()) {
      newErrors.description = "Description is required";
    } else if (description.trim().length > 1000) {
      newErrors.description = "Description must be at most 1000 characters";
    }

    if (!address1.trim()) {
      newErrors.address1 = "Address line 1 is required";
    } else if (address1.trim().length > 255) {
      newErrors.address1 = "Address line 1 must be at most 255 characters";
    }

    if (!address2.trim()) {
      newErrors.address2 = "Address line 2 is required";
    } else if (address2.trim().length > 255) {
      newErrors.address2 = "Address line 2 must be at most 255 characters";
    }

    if (!city.trim()) {
      newErrors.city = "City is required";
    } else if (city.trim().length > 100) {
      newErrors.city = "City must be at most 100 characters";
    }

    if (!state) {
      newErrors.state = "State is required";
    }

    if (!pincode.trim()) {
      newErrors.pincode = "Pincode is required";
    } else {
      const pincodeNum = Number(pincode);
      if (isNaN(pincodeNum) || pincodeNum < 100000 || pincodeNum > 999999) {
        newErrors.pincode = "Pincode must be a 6-digit number";
      }
    }

    if (latitude === null || longitude === null) {
      newErrors.location = "Location on map is required";
    }

    const minAmount = parseFloat(workAmountMin);
    const maxAmount = parseFloat(workAmountMax);

    if (Number.isNaN(minAmount) || Number.isNaN(maxAmount)) {
      newErrors.workAmount = "Work amount is required";
    } else if (
      minAmount < 0 ||
      maxAmount < 0 ||
      minAmount > 10000000 ||
      maxAmount > 10000000
    ) {
      newErrors.workAmount = "Work amount must be between 0 and 10,000,000";
    } else if (maxAmount < minAmount) {
      newErrors.workAmount = "Maximum amount must be greater than or equal to minimum amount";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      // Scroll to the first error
      const firstErrorField = document.querySelector('.form-field .error-message');
      if (firstErrorField) {
        const parentField = firstErrorField.closest('.form-field');
        if (parentField) {
          parentField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const inputToFocus = parentField.querySelector('input.error, select.error, textarea.error');
          if (inputToFocus) {
            inputToFocus.focus();
          }
        }
      }
      return;
    }

    setSubmitLoading(true);

    try {
      const formData = new FormData();
      
      // If quotes are present, only send quote_last_date
      if (hasQuotes && workStatus === "open") {
        formData.append("quote_last_date", quoteLastDate);
      } else {
        // Send all fields if no quotes or work is not open
        formData.append("title", title.trim());
        formData.append("sub_categories", JSON.stringify(selectedSubCategories));
        formData.append("specialisations", JSON.stringify(selectedSpecialisations));
        formData.append("quote_last_date", quoteLastDate);
        formData.append("description", description.trim());
        formData.append("address1", address1.trim());
        formData.append("address2", address2.trim());
        formData.append("city", city.trim());
        formData.append("pincode", Number(pincode));
        formData.append("state", state);
        formData.append("location_coordinates", JSON.stringify([Number(longitude), Number(latitude)])); // API expects [lng, lat]
        formData.append(
          "work_amount",
          JSON.stringify({
            min: parseFloat(workAmountMin),
            max: parseFloat(workAmountMax),
          })
        );

        documents.forEach((file) => {
          formData.append("file", file);
        });
      }

      const response = await fetch(`${USER_BASE_URL}/${VERSION}/work/edit/${id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "app-version": "1.0.0",
        },
        body: formData,
      });

      const data = await response.json();

      if (data && data.response_code === 200) {
        showToast(t.submitSuccess, "success");
        navigate("/dashboard");
      } else {
        showToast(data?.message || t.submitError, "error");
      }
    } catch (error) {
      console.error("Error updating work:", error);
      showToast(error.message || t.submitError, "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderSelectedSubCategories = () => {
    if (!selectedSubCategories.length) return null;
    const isSubCategoriesDisabled = isFieldDisabled("sub_categories");

    return (
      <div className="selected-subcategories-container">
        <h4 className="section-subheading">{t.selectedSubCategories}</h4>
        <div className="selected-subcategories-chips">
          {selectedSubCategories.map((subCatId) => {
            const subCat = activeCategory?.sub_categories?.find((sc) => sc._id === subCatId);
            if (!subCat) return null;
            return (
              <span key={subCatId} className="selected-subcategory-chip">
                {subCat.name}
                <button 
                  type="button" 
                  onClick={() => {
                    if (!isSubCategoriesDisabled) {
                      removeSubCategory(subCatId);
                    }
                  }}
                  disabled={isSubCategoriesDisabled}
                  aria-label="Remove"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSelectedSpecialisations = () => {
    if (!selectedSpecialisations.length) return null;
    const isSpecsDisabled = isFieldDisabled("specialisations");

    return (
      <div className="selected-subcategories-container">
        <h4 className="section-subheading">{t.selectedSpecialisations}</h4>
        <div className="selected-subcategories-chips">
          {selectedSpecialisations.map((specId) => {
            const spec = specialisations.find((s) => s._id === specId);
            if (!spec) return null;
            return (
              <span key={specId} className="selected-subcategory-chip">
                {spec.name}
                <button
                  type="button"
                  onClick={() => {
                    if (!isSpecsDisabled) {
                      removeSpecialisation(specId);
                    }
                  }}
                  disabled={isSpecsDisabled}
                  aria-label="Remove"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  // Check if fields should be disabled (when has quotes, only quote_last_date is editable)
  const isFieldDisabled = (fieldName) => {
    if (hasQuotes && workStatus === "open") {
      return fieldName !== "quote_last_date";
    }
    return false;
  };

  // Show loading until categories are loaded and work details are fetched
  if (loading || !categoriesLoaded) {
    return (
      <div className="add-work-loading" dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (workNotFound || !id) {
    return (
      <div className="add-work-container" dir={lang === "ar" ? "rtl" : "ltr"}>
        <Header lang={lang} />
        <div className="add-work-page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <i className="bx bx-arrow-back"></i>
          </button>
          <h1 className="add-work-page-title">{t.pageTitle}</h1>
          <div className="header-spacer"></div>
        </div>
        <div className="add-work-content" style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
          gap: "16px",
          textAlign: "center",
        }}>
          <img src={noWorkImage} alt="No work" style={{ maxWidth: "300px", width: "100%" }} />
          <p style={{ fontSize: "16px", color: "#6b7280", margin: 0 }}>{t.workNotFound}</p>
        </div>
        <BottomNavbar currentPage="add" lang={lang} />
      </div>
    );
  }

  return (
    <div className="add-work-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Header lang={lang} />

      <div className="add-work-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="add-work-page-title">{t.pageTitle}</h1>
        <div className="header-spacer"></div>
      </div>

      <div className="add-work-content">
        {/* Warning banner when quotes are present */}
        {hasQuotes && workStatus === "open" && (
          <div className="quotes-warning-banner">
            <i className="bx bx-info-circle"></i>
            <p>{t.quotesWarning}</p>
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate>
          <section className="form-section">
            <h2 className="section-heading">{t.heading}</h2>

            <div ref={titleFieldRef} className="form-field">
              <label className="form-label">{t.title}</label>
              <input
                type="text"
                className={`form-input ${errors.title ? "error" : ""}`}
                placeholder={t.titlePlaceholder}
                value={title}
                maxLength={50}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) setErrors((prev) => ({ ...prev, title: null }));
                }}
                disabled={isFieldDisabled("title")}
              />
              {errors.title && <span className="error-message">{errors.title}</span>}
            </div>

            <div ref={categoryFieldRef} className="form-field">
              <label className="form-label">{t.categories}</label>
              {categories.length ? (
                <div className="category-chips">
                  {categories.map((category) => (
                    <button
                      key={category._id}
                      type="button"
                      className={`category-chip ${
                        selectedCategoryId === category._id ? "selected" : ""
                      }`}
                      onClick={() => {
                        if (isFieldDisabled("category")) return;
                        setSelectedCategoryId((prev) =>
                          prev === category._id ? prev : category._id
                        );
                        if (selectedCategoryId !== category._id) {
                          setSelectedSubCategories([]);
                        }
                        if (errors.category) {
                          setErrors((prev) => ({ ...prev, category: null }));
                        }
                      }}
                      disabled={isFieldDisabled("category")}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="form-info">{t.noCategories}</p>
              )}
              {errors.category && <span className="error-message">{errors.category}</span>}
            </div>

            {selectedCategoryId && (
              <div ref={subCategoriesFieldRef} className="form-field">
                <label className="form-label">{t.subCategories}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={t.searchSubCategories}
                  value={subCategorySearch}
                  onChange={(e) => setSubCategorySearch(e.target.value)}
                  disabled={isFieldDisabled("subCategories")}
                />

                {renderSelectedSubCategories()}

                <div className="subcategories-list">
                  {filteredSubCategories.map((subCat) => {
                    const isSelected = selectedSubCategories.includes(subCat._id);
                    return (
                      <label key={subCat._id} className={`sub-category-item ${isSelected ? "selected" : ""}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            if (isFieldDisabled("subCategories")) return;
                            toggleSubCategory(subCat._id);
                            if (errors.subCategories) {
                              setErrors((prev) => ({ ...prev, subCategories: null }));
                            }
                          }}
                          disabled={isFieldDisabled("subCategories")}
                        />
                        <span>{subCat.name}</span>
                      </label>
                    );
                  })}
                </div>
                {errors.subCategories && (
                  <span className="error-message">{errors.subCategories}</span>
                )}
              </div>
            )}

            {specialisations.length > 0 && (
              <div ref={specialisationsFieldRef} className="form-field">
                <label className="form-label">{t.specialisations}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={t.searchSubCategories}
                  value={specialisationSearch}
                  onChange={(e) => setSpecialisationSearch(e.target.value)}
                  disabled={isFieldDisabled("specialisations")}
                />

                {renderSelectedSpecialisations()}

                <div className="subcategories-list">
                  {filteredSpecialisations.map((spec) => {
                    const isSelected = selectedSpecialisations.includes(spec._id);
                    return (
                      <label
                        key={spec._id}
                        className={`sub-category-item ${isSelected ? "selected" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            if (isFieldDisabled("specialisations")) return;
                            toggleSpecialisation(spec._id);
                            if (errors.specialisations) {
                              setErrors((prev) => ({ ...prev, specialisations: null }));
                            }
                          }}
                          disabled={isFieldDisabled("specialisations")}
                        />
                        <span>{spec.name}</span>
                      </label>
                    );
                  })}
                </div>
                {errors.specialisations && (
                  <span className="error-message">{errors.specialisations}</span>
                )}
              </div>
            )}

            <div ref={quoteDateFieldRef} className="form-field">
              <label className="form-label">{t.lastDate}</label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  className={`form-input date-input ${errors.quoteLastDate ? "error" : ""}`}
                  placeholder={t.selectDate}
                  min={minDate}
                  max={maxDate}
                  value={quoteLastDate}
                  onChange={(e) => {
                    setQuoteLastDate(e.target.value);
                    if (errors.quoteLastDate) {
                      setErrors((prev) => ({ ...prev, quoteLastDate: null }));
                    }
                  }}
                />
                <i className="bx bx-calendar date-icon"></i>
              </div>
              {errors.quoteLastDate && (
                <span className="error-message">{errors.quoteLastDate}</span>
              )}
            </div>

            <div ref={descriptionFieldRef} className="form-field">
              <label className="form-label">{t.description}</label>
              <textarea
                className={`form-textarea ${errors.description ? "error" : ""}`}
                placeholder={t.descriptionPlaceholder}
                value={description}
                rows={5}
                maxLength={1000}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) {
                    setErrors((prev) => ({ ...prev, description: null }));
                  }
                }}
                disabled={isFieldDisabled("description")}
              />
              {errors.description && (
                <span className="error-message">{errors.description}</span>
              )}
            </div>

            <div ref={workAmountFieldRef} className="form-field">
              <label className="form-label">{t.workAmount}</label>
              <div className="work-amount-grid">
                <div className="work-amount-field">
                  <span className="work-amount-label">{t.minAmount}</span>
                  <input
                    type="number"
                    className={`form-input ${errors.workAmount ? "error" : ""}`}
                    min="0"
                    step="0.01"
                    value={workAmountMin}
                    onChange={(e) => {
                      setWorkAmountMin(e.target.value);
                      if (errors.workAmount) {
                        setErrors((prev) => ({ ...prev, workAmount: null }));
                      }
                    }}
                    disabled={isFieldDisabled("workAmount")}
                  />
                </div>
                <div className="work-amount-field">
                  <span className="work-amount-label">{t.maxAmount}</span>
                  <input
                    type="number"
                    className={`form-input ${errors.workAmount ? "error" : ""}`}
                    min="0"
                    step="0.01"
                    value={workAmountMax}
                    onChange={(e) => {
                      setWorkAmountMax(e.target.value);
                      if (errors.workAmount) {
                        setErrors((prev) => ({ ...prev, workAmount: null }));
                      }
                    }}
                    disabled={isFieldDisabled("workAmount")}
                  />
                </div>
              </div>
              {errors.workAmount && <span className="error-message">{errors.workAmount}</span>}
            </div>
          </section>

          <section className="form-section">
            <h2 className="section-heading">{t.documents}</h2>
            <p className="form-info">{t.documentsInfo}</p>

            {/* Existing Documents */}
            {existingDocuments.length > 0 && (
              <div className="document-grid" style={{ marginBottom: "16px" }}>
                {existingDocuments.map((doc) => (
                  <div key={doc.id} className="document-item">
                    <div className="document-icon">
                      {doc.url && doc.url.includes("pdf") ? "PDF" : "FILE"}
                    </div>
                    <button
                      type="button"
                      className="document-delete-btn"
                      onClick={() => handleDeleteDocument(doc.id)}
                      disabled={deletingDocumentId === doc.id || isFieldDisabled("documents")}
                      aria-label="Delete document"
                    >
                      {deletingDocumentId === doc.id ? (
                        <div className="button-spinner"></div>
                      ) : (
                        <i className="bx bx-x"></i>
                      )}
                    </button>
                    <p className="document-name">
                      {doc.id?.split("/").pop()?.substring(0, 15) || "Document"}
                      ...
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="file-upload-section">
              <input
                ref={documentInputRef}
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.svg,.webp,.avif,.heic,.heif,.pdf,.doc,.docx"
                onChange={handleDocumentSelect}
                className="file-input-hidden"
                disabled={existingDocuments.length + documents.length >= MAX_DOCUMENTS || isFieldDisabled("documents")}
              />

              {documents.length > 0 && (
                <div className="document-grid">
                  {documents.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="document-item">
                      <div className="document-icon">
                        {file.type.includes("pdf")
                          ? "PDF"
                          : file.type.includes("word")
                          ? "DOC"
                          : file.type.includes("png")
                          ? "PNG"
                          : file.type.includes("jpeg") || file.type.includes("jpg")
                          ? "JPG"
                          : "FILE"}
                      </div>
                      <button
                        type="button"
                        className="document-delete-btn"
                        onClick={() => removeDocument(index)}
                        disabled={isFieldDisabled("documents")}
                        aria-label="Remove document"
                      >
                        <i className="bx bx-x"></i>
                      </button>
                      <p className="document-name">
                        {file.name.length > 18 ? `${file.name.slice(0, 15)}...` : file.name}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {existingDocuments.length + documents.length < MAX_DOCUMENTS && (
                <div
                  className="file-upload-area"
                  onClick={() => {
                    if (!isFieldDisabled("documents")) {
                      documentInputRef.current?.click();
                    }
                  }}
                  role="button"
                  tabIndex={isFieldDisabled("documents") ? -1 : 0}
                  onKeyDown={(e) => {
                    if (!isFieldDisabled("documents") && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      documentInputRef.current?.click();
                    }
                  }}
                  disabled={isFieldDisabled("documents")}
                >
                  <p>{t.clickToUpload}</p>
                </div>
              )}
            </div>
          </section>

          <section className="form-section">
            <h2 className="section-heading">{t.locationDetails}</h2>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={sameAsProfile}
                onChange={(e) => handleSameAsProfileToggle(e.target.checked)}
                disabled={isFieldDisabled("location")}
              />
              <span>{t.sameAsProfile}</span>
            </label>

            <div ref={address1FieldRef} className="form-field">
              <label className="form-label">{t.address1}</label>
              <input
                type="text"
                className={`form-input ${errors.address1 ? "error" : ""}`}
                value={address1}
                maxLength={255}
                onChange={(e) => {
                  setAddress1(e.target.value);
                  if (errors.address1) setErrors((prev) => ({ ...prev, address1: null }));
                }}
                disabled={(sameAsProfile && profile && hasAppliedProfileAddress) || isFieldDisabled("address1")}
              />
              {errors.address1 && <span className="error-message">{errors.address1}</span>}
            </div>

            <div ref={address2FieldRef} className="form-field">
              <label className="form-label">{t.address2}</label>
              <input
                type="text"
                className={`form-input ${errors.address2 ? "error" : ""}`}
                value={address2}
                maxLength={255}
                onChange={(e) => {
                  setAddress2(e.target.value);
                  if (errors.address2) setErrors((prev) => ({ ...prev, address2: null }));
                }}
                disabled={(sameAsProfile && profile && hasAppliedProfileAddress) || isFieldDisabled("address2")}
              />
              {errors.address2 && <span className="error-message">{errors.address2}</span>}
            </div>

            <div ref={cityFieldRef} className="form-field">
              <label className="form-label">{t.city}</label>
              <input
                type="text"
                className={`form-input ${errors.city ? "error" : ""}`}
                value={city}
                maxLength={100}
                onChange={(e) => {
                  setCity(e.target.value);
                  if (errors.city) setErrors((prev) => ({ ...prev, city: null }));
                }}
                disabled={(sameAsProfile && profile && hasAppliedProfileAddress) || isFieldDisabled("city")}
              />
              {errors.city && <span className="error-message">{errors.city}</span>}
            </div>

            <div ref={stateFieldRef} className="form-field">
              <label className="form-label">{t.state}</label>
              <select
                className={`form-input ${errors.state ? "error" : ""}`}
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  if (errors.state) setErrors((prev) => ({ ...prev, state: null }));
                }}
                disabled={(sameAsProfile && profile && hasAppliedProfileAddress) || isFieldDisabled("state")}
              >
                <option value="">{t.selectState}</option>
                {EMIRATES.map((emirate) => (
                  <option key={emirate} value={emirate}>
                    {emirate}
                  </option>
                ))}
              </select>
              {errors.state && <span className="error-message">{errors.state}</span>}
            </div>

            <div ref={pincodeFieldRef} className="form-field">
              <label className="form-label">{t.pincode}</label>
              <input
                type="text"
                inputMode="numeric"
                className={`form-input ${errors.pincode ? "error" : ""}`}
                value={pincode}
                maxLength={6}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  setPincode(digits);
                  if (errors.pincode) setErrors((prev) => ({ ...prev, pincode: null }));
                }}
                disabled={(sameAsProfile && profile && hasAppliedProfileAddress) || isFieldDisabled("pincode")}
              />
              {errors.pincode && <span className="error-message">{errors.pincode}</span>}
            </div>

            <div
              ref={locationFieldRef}
              className={`form-field ${errors.location ? "has-error" : ""}`}
            >
              <label className="form-label">{t.locationOnMap}</label>
              <button
                type="button"
                className={`location-select-btn ${latitude !== null && longitude !== null ? "selected" : ""}`}
                onClick={() => {
                  if (!isFieldDisabled("location")) {
                    setShowLocationPicker(true);
                  }
                }}
                disabled={isFieldDisabled("location")}
              >
                {latitude !== null && longitude !== null ? t.locationSelected : t.selectLocation}
              </button>

              {latitude !== null && longitude !== null && (
                <div className="location-selected-values">
                  <span>
                    Lat: {latitude.toFixed(6)}
                  </span>
                  <span>
                    Lng: {longitude.toFixed(6)}
                  </span>
                </div>
              )}

              {errors.location && <span className="error-message">{errors.location}</span>}
            </div>
          </section>

          <button type="submit" className="add-work-submit-btn" disabled={submitLoading}>
            {submitLoading ? (
              <>
                <div className="button-spinner"></div>
                {t.loading}
              </>
            ) : (
              t.addWork
            )}
          </button>
        </form>
      </div>

      <BottomNavbar currentPage="add" lang={lang} />

      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onSelect={handleLocationSelect}
        initialLat={latitude}
        initialLng={longitude}
        lang={lang}
      />
    </div>
  );
}

